import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { roleHasAccess } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../hooks/useFormErrors';
import { combine, email as emailRule, required, runValidators } from '../../lib/validate';
import styles from './Auth.module.scss';

type FieldKey = 'email' | 'password';

export default function LoginPage(): JSX.Element {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { errors, setErrors, clearError } = useFormErrors<FieldKey>();

  const validate = (): Partial<Record<FieldKey, string>> =>
    runValidators<FieldKey>(
      {
        email: combine(required, emailRule),
        password: required,
      },
      { email, password },
    );

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
      const fromState = (location.state as { from?: string } | null)?.from;
      const user = useAuthStore.getState().user;
      const isStaff = user ? roleHasAccess(user.role, 'store_manager') : false;
      const fallback = isStaff ? '/admin' : '/conta';
      navigate(fromState ?? fallback, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao iniciar sessão');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={t.auth.loginTitle} noindex />
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <header className={styles.intro}>
            <p className={styles.eyebrow}>Bem-vinda de volta</p>
            <h1 className={styles.title}>{t.auth.loginTitle}</h1>
            <p className={styles.lead}>Entra na tua conta para acompanhar encomendas e moradas.</p>
          </header>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <Field label={t.auth.email} required error={errors.email} className={styles.field}>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) clearError('email');
                }}
                autoComplete="email"
              />
            </Field>
            <Field label={t.auth.password} required error={errors.password} className={styles.field}>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) clearError('password');
                }}
                autoComplete="current-password"
              />
            </Field>
            {error ? <p className={styles.error}>{error}</p> : null}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
              {submitting ? t.common.loading : t.auth.login}
            </Button>
          </form>

          <div className={styles.links}>
            <Link to="/conta/recuperar">{t.auth.forgotPassword}</Link>
            <span>
              {t.auth.noAccount}{' '}
              <Link to="/conta/registo" state={location.state}>
                {t.nav.register}
              </Link>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
